import { collection, doc, setDoc, Timestamp, query, where, getDocs} from "firebase/firestore";
import { firestore } from "../firebase/firebaseConfig";

/**
 * Creates a new feedback entry in the `feedback` collection.
 * @param {string} feedbackId - Unique identifier for the feedback.
 * @param {Object} feedbackDetails - Details of the feedback (e.g., reviewerId, revieweeId, transactionId).
 * @returns {Promise<void>}
 */
export const createFeedback = async (feedbackId, feedbackDetails) => {
  try {
    const feedbackRef = doc(firestore, "feedback", feedbackId);
    const feedbackData = {
      ...feedbackDetails,
      createdAt: Timestamp.now(),
    };
    await setDoc(feedbackRef, feedbackData);
    console.log(`[createFeedback] Feedback created successfully with ID: ${feedbackId}`);
  } catch (error) {
    console.error("[createFeedback] Error creating feedback:", error);
    throw new Error("Failed to create feedback. Please try again.");
  }
};

/**
 * Fetches feedback for a specific transaction.
 * @param {string} transactionId - The unique ID of the transaction.
 * @returns {Promise<Array>} An array of feedback entries.
 */
export const fetchFeedbackByTransaction = async (transactionId) => {
  try {
    const feedbackRef = collection(firestore, "feedback");
    const transactionQuery = query(feedbackRef, where("transactionId", "==", transactionId));
    const querySnapshot = await getDocs(transactionQuery);

    if (querySnapshot.empty) {
      console.warn(`[fetchFeedbackByTransaction] No feedback found for transaction ID: ${transactionId}`);
      return [];
    }

    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("[fetchFeedbackByTransaction] Error fetching feedback by transaction:", error);
    throw new Error("Failed to fetch feedback for transaction.");
  }
};

/**
 * Fetches feedback given to or submitted by a specific user.
 * @param {string} userId - The unique ID of the user.
 * @param {string} type - Type of feedback to fetch (`reviewer` or `reviewee`).
 * @returns {Promise<Array>} An array of feedback entries.
 */
export const fetchFeedbackForUser = async (userId, type) => {
  try {
    const feedbackRef = collection(firestore, "feedback");
    const userQuery = query(feedbackRef, where(type === "reviewer" ? "reviewerId" : "revieweeId", "==", userId));
    const querySnapshot = await getDocs(userQuery);

    if (querySnapshot.empty) {
      console.warn(`[fetchFeedbackForUser] No feedback found for user ID: ${userId}`);
      return [];
    }

    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("[fetchFeedbackForUser] Error fetching feedback for user:", error);
    throw new Error("Failed to fetch feedback for user.");
  }
};

/**
 * Calculates the average rating for a user.
 * @param {string} userId - The unique ID of the user.
 * @returns {Promise<number>} The average rating for the user.
 */
export const calculateUserRating = async (userId) => {
    try {
      const feedback = await fetchFeedbackForUser(userId, "reviewee");
  
      if (!feedback.length) {
        console.warn(`[calculateUserRating] No feedback found for user ID: ${userId}`);
        return 0; // Default rating if no feedback exists
      }
  
      const totalRating = feedback.reduce((sum, entry) => sum + entry.rating, 0);
      return totalRating / feedback.length;
    } catch (error) {
      console.error("[calculateUserRating] Error calculating user rating:", error);
      throw new Error("Failed to calculate user rating.");
    }
};

export const initializeFeedback = async (userId) => {
  try {
    const feedbackDocRef = doc(firestore, "feedback", userId);
    await setDoc(feedbackDocRef, {
      buyerFeedback: null,
      sellerFeedback: null,
      isFeedbackComplete: false,
    });
    console.log("Initialized feedback document for userId:", userId);
  } catch (error) {
    console.error("Error initializing feedback document:", error);
  }
};

